import { Alert } from '@creditornot/cb-alert';
import { Button } from '@creditornot/cb-button';
import { typographyCss } from '@creditornot/cb-ingredients';
import { InputAlert } from '@creditornot/cb-input';
import { Modal, ModalContent } from '@creditornot/cbt-modal';
import { useMutation } from '@tanstack/react-query';
import React, { useCallback, useState } from 'react';
import toast from 'react-hot-toast';
import { FormattedMessage, useIntl } from 'react-intl';
import styled from 'styled-components';

import { importXmenuJson } from 'apis/offer-listing-service/api';
import { APIErrorNotification } from 'common/components';
import { PartialExportedMenu, readAndParseFile } from 'modules/menu/components/ImportMenuModal/utils';
import { deleteSharedMenu } from 'modules/shared-menu/api';
import { useScopeContext } from 'modules/shared-menu/contexts/ScopeContext';
import { useSelectionInfoContext } from 'modules/shared-menu/contexts/SelectionInfoContext';
import { trackSelectXmenuJSONForImport } from 'modules/shared-menu/telemetry';

import { XmenuJsonDropzone } from '../../SharedMenuEditorPage/components/Collections/VenueXmenuImport/XmenuJsonDropzone';

type Props = {
  open: boolean;
  onClose: () => void;
};

const StyledFooter = styled.div`
  width: 100%;
  display: flex;
  justify-content: flex-end;
  gap: 16px;
`;

const Description = styled.div`
  margin-block-end: 24px;
  ${typographyCss.Body3()};
`;

const Warning = styled(Alert)`
  margin-block-start: 24px;
`;

export const ImportFromXmenuModal = ({ open, onClose }: Props) => {
  const intl = useIntl();

  const [fileName, setFileName] = useState<string | undefined>(undefined);
  const [fileError, setFileError] = useState<string | undefined>(undefined);
  const [importData, setImportData] = useState<PartialExportedMenu | undefined>(undefined);

  const scope = useScopeContext();
  const { vlmeConfig, selection } = useSelectionInfoContext();

  const { mutateAsync: executeDeleteSelection } = useMutation(() => deleteSharedMenu(selection.id));

  const {
    mutate: executeImportXmenuFile,
    isLoading: importingXmenuFile,
    error: importError,
    reset,
  } = useMutation(
    async () => {
      const venue = vlmeConfig?.venue;

      if (!importData || scope.type !== 'venue' || !venue) {
        return Promise.reject();
      }
      await executeDeleteSelection();

      return importXmenuJson({
        currency: venue?.currency,
        // adds a default name to single venue's menus (not displayed in the UI)
        selection_name: `${venue?.slug}'s menu`,
        scope,
        menu_request: importData,
      });
    },
    {
      onSuccess: () => {
        handleModalClose();
        toast.success(<FormattedMessage id="listing-manager.selections.venue-editor.xmenu-import.success-message" />);
        window.location.reload();
      },
    },
  );

  const handleModalClose = useCallback(() => {
    reset();
    onClose();
    setFileError(undefined);
    setFileName(undefined);
    setImportData(undefined);
  }, [reset, onClose]);

  const handleFileChange = async (files: File[]) => {
    if (files.length > 0) {
      await readAndParseFile(files[0], intl)
        .then(menuImportData => setImportData(menuImportData))
        .then(() => trackSelectXmenuJSONForImport('Success'))
        .catch(error => {
          setFileError(error);
          trackSelectXmenuJSONForImport(`Failure: ${error}`);
        });
    }
  };

  return (
    <Modal
      open={open}
      onDismiss={handleModalClose}
      title={intl.formatMessage({ id: 'listing-manager.selections.venue-editor.xmenu-import.action-label' })}
      sizePreset="centered:large"
      disableDismissOnOutsidePress
      footer={
        <StyledFooter>
          <Button size="small" variant="secondary" onClick={handleModalClose} disabled={importingXmenuFile}>
            <FormattedMessage id="state.cancel" />
          </Button>
          <Button
            size="small"
            variant="primary"
            onClick={() => executeImportXmenuFile()}
            disabled={!importData || !!fileError}
            loading={importingXmenuFile}
          >
            <FormattedMessage id="state.confirm" />
          </Button>
        </StyledFooter>
      }
    >
      <ModalContent disablePadding>
        <Description>
          <FormattedMessage id="listing-manager.selections.venue-editor.xmenu-import.action-description" />
        </Description>

        <XmenuJsonDropzone
          fileName={fileName}
          onDrop={files => {
            setFileError(undefined);
            setFileName(files[0].name);
            if (!files[0].name.endsWith('.json')) {
              setFileError(intl.formatMessage({ id: 'menu.fileMustBeJson' }));
              trackSelectXmenuJSONForImport('Failure: wrong file extension');
            } else {
              handleFileChange(files);
            }
          }}
          hasError={!!fileError}
          loading={false}
        />
        {!!fileError && (
          <InputAlert style={{ marginBlockStart: 8 }} variant="error">
            {fileError}
          </InputAlert>
        )}

        {!!importError && <APIErrorNotification style={{ marginBlockStart: 24 }} error={importError} />}

        <Warning
          title={intl.formatMessage({ id: 'listing-manager.selections.venue-editor.xmenu-import.action-warning' })}
          variant="warning"
        />
      </ModalContent>
    </Modal>
  );
};
